import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import Echo from 'laravel-echo';
import { environment } from 'environments/environment';
import Pusher, { Channel } from 'pusher-js';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { AuthService } from './auth/auth.service';

(window as any).Pusher = Pusher;

// Mirror GlobalErrorHandler's reporting window: reconnect/auth loops can fire the
// same error repeatedly, so skip identical messages seen within this window to
// keep an error storm from hammering POST /client-errors.
const REPORT_DEDUPE_MS = 10000;

@Injectable({
    providedIn: 'root',
})
export class EchoService implements OnDestroy
{
    private _echo: Echo<any> | null = null;
    private _activeChannels: Map<string, any> = new Map();
    private _isConnected$ = new BehaviorSubject<boolean>(false);
    private _destroy$ = new Subject<void>();
    private readonly _recentReports = new Map<string, number>();

    constructor(
            private _authService: AuthService,
    ) {
        // EchoService initialized
    }

    public channel(channel: string) {
        const echoChannel = this.getEchoInstance().channel(channel);
        this._activeChannels.set(channel, { type: 'public', channel: echoChannel });
        return echoChannel;
    }

    public private(channel: string) {
        const echoChannel = this.getEchoInstance().private(channel);
        this._activeChannels.set(channel, { type: 'private', channel: echoChannel });
        return echoChannel;
    }

    public leave(channel: string) {
        try {
            const result = this.getEchoInstance().leave(channel);
            this._activeChannels.delete(channel);
            return result;
        } catch (error) {
            // Benign teardown race — caller still gets the rethrown error; keep a
            // dev-only breadcrumb. The !environment.production gate below keeps it
            // out of the prod console (SD-7666 removed SD-7544's global console mask).
            if (!environment.production) {
                console.error(`EchoService: Error leaving channel ${channel}:`, error);
            }
            throw error;
        }
    }

    public leaveChannel(channel: string) {
        try {
            const result = this.getEchoInstance().leaveChannel(channel);
            this._activeChannels.delete(channel);
            return result;
        } catch (error) {
            // Benign teardown race — caller still gets the rethrown error; keep a
            // dev-only breadcrumb. The !environment.production gate below keeps it
            // out of the prod console (SD-7666 removed SD-7544's global console mask).
            if (!environment.production) {
                console.error(`EchoService: Error leaving channel ${channel}:`, error);
            }
            throw error;
        }
    }

    public getSocketId() {
        if (this._echo && this._echo.socketId()) {
            return this._echo.socketId();
        }
        return null;
    }

    /**
     * Get list of active channels for debugging
     */
    public getActiveChannels(): string[] {
        return Array.from(this._activeChannels.keys());
    }

    /**
     * Get detailed information about active channels
     */
    public getChannelDetails(): any[] {
        return Array.from(this._activeChannels.entries()).map(([name, details]) => ({
            name,
            type: details.type,
            hasChannel: !!details.channel
        }));
    }

    /**
     * Force disconnect and destroy the Echo instance
     * Useful for testing and org changes
     */
    public destroy(): void {
        // Leave all active channels first
        const channelsToLeave = Array.from(this._activeChannels.keys());

        channelsToLeave.forEach(channelName => {
            try {
                this.leave(channelName);
            } catch (error) {
                // Benign teardown race — best-effort cleanup continues for the
                // remaining channels; dev-only breadcrumb.
                if (!environment.production) {
                    console.error(`EchoService: Error leaving channel ${channelName} during destroy:`, error);
                }
            }
        });

        // Disconnect and destroy Echo instance
        if (this._echo) {
            try {
                this._echo.disconnect();
            } catch (error) {
                this.reportError('EchoService: Error disconnecting Echo', error);
            }
            this._echo = null;
        }

        this._activeChannels.clear();
        this._isConnected$.next(false);
    }

    ngOnDestroy(): void {
        this.destroy();
        this._destroy$.complete();
        this._isConnected$.complete();
    }

    /**
     * Manual cleanup method for testing purposes
     * (Root services' ngOnDestroy is never called automatically)
     */
    public manualCleanup(): void {
        this.destroy();
    }

    private getEchoInstance() {
        // TODO - fix this so it works on org change
        if (!this._echo) {

            const customAuthorizer = (channel: Channel, options: any): { authorize: Function } => {
                return {
                    authorize: async (socketId: string, callback: Function): Promise<void> => {
                        try {
                            const response = await fetch(environment.LegFiCoreApi + '/broadcasting/auth', {
                                method: 'POST',
                                body: JSON.stringify({
                                    socket_id: socketId,
                                    channel_name: channel.name,
                                }),
                                credentials: 'include',
                                headers: {
                                    'Accept': 'application/json',
                                    'Content-Type': 'application/json',
                                    'Authorization': 'Bearer ' + LegFiJwtService.raw(),
                                    'X-XSRF-TOKEN': this._authService.getCookie('XSRF-TOKEN'),
                                    'X-Legfi-Site-Id': environment.SiteId.toString(),
                                }
                            });

                            const data = await response.json();

                            // Echo expects these callback signatures in the case of success or failure
                            if (data.auth) {
                                callback(null, data);
                            } else {
                                this.reportError(`EchoService: Authorization failed for channel: ${channel.name}`);
                                callback(new Error('Authorization failed'), null);
                            }
                        } catch (error) {
                            // Handle fetch errors
                            this.reportError(`EchoService: Authorization error for channel ${channel.name}`, error);
                            callback(error, null);
                        }
                    }
                };
            };

            this._echo = new Echo({
                broadcaster: 'pusher',
                key: environment.PusherAppKey,
                cluster: environment.PusherAppCluster,
                forceTLS: true,
                host: environment.LegFiCoreApi,
                authorizer: customAuthorizer,
            });

            // Set up connection monitoring
            this.setupConnectionMonitoring();
        }
        return this._echo;
    }

    private setupConnectionMonitoring(): void {
        if (this._echo?.connector?.socket) {
            this._echo.connector.socket.on('connect', () => {
                this._isConnected$.next(true);
            });

            this._echo.connector.socket.on('disconnect', () => {
                this._isConnected$.next(false);
            });

            this._echo.connector.socket.on('error', (error: any) => {
                this.reportError('EchoService: WebSocket error', error);
            });
        }
    }

    /**
     * Hook for actionable Echo errors. The Better Stack client-error sink was
     * removed (SD-7666); this now emits a dev-only breadcrumb (gated by
     * !environment.production), with the dedupe bookkeeping keeping a
     * reconnect/auth storm from flooding the dev console with the same message
     * within REPORT_DEDUPE_MS.
     */
    private reportError(message: string, error?: unknown): void {
        const now = Date.now();
        const lastReportedAt = this._recentReports.get(message);

        if (lastReportedAt !== undefined && now - lastReportedAt < REPORT_DEDUPE_MS) {
            return;
        }

        this.pruneRecentReports(now);
        this._recentReports.set(message, now);

        if (!environment.production) {
            console.error(message, error);
        }
    }

    private pruneRecentReports(now: number): void {
        for (const [message, reportedAt] of this._recentReports) {
            if (now - reportedAt >= REPORT_DEDUPE_MS) {
                this._recentReports.delete(message);
            }
        }
    }
}
